import CircleCheck from '@avanta/shared/assets/circle-check.svg';
import { Text } from '@avanta/shared/components/Text';
import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { ChevronRightIcon, FolderOpenIcon, Headset } from 'lucide-react';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useLocation, useNavigate } from 'react-router';
import Logo from '@/assets/itae-agendar.svg';
import { ROUTES } from 'constants/routes';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { RecordNumberSkeleton } from './RecordNumberSkeleton';

export const AppointmentsSuccess = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const location = useLocation();
  const patientId = location.state?.patientId ?? '';
  const hasExpedient = location.state?.hasExpedient ?? false;
  const { data: patientRecord } = useFetchActivePatientRecord(patientId, { polling: true });
  const navigate = useNavigate();
  const patientRecordCode = patientRecord?.code;

  const handleOnNavigate = useCallback((): void => {
    navigate(generatePath(ROUTES.PATIENT_PROFILE, { id: patientId }));
  }, [navigate, patientId]);

  useEffect(() => {
    if (!patientRecordCode) {
      return;
    }
    const timeoutId = setTimeout(() => {
      handleOnNavigate();
    }, 5000);

    return (): void => clearTimeout(timeoutId);
  }, [handleOnNavigate, patientRecordCode]);

  const body = !hasExpedient
    ? t('AppointmentsSuccess.notExpedient.body')
    : t('AppointmentsSuccess.hasExpedient.body');

  return (
    <>
      <header className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-18">
        <Link to={ROUTES.HOME} className="flex items-center">
          <img src={Logo} alt="itae" className="h-12 w-auto object-contain" />
        </Link>
        <Button
          variant="terciary"
          // eslint-disable-next-line no-alert
          onClick={() => window.alert('Aún no implementado')}
          icon="leading"
        >
          <Headset className="size-5" />
          {t('Nav.support', { ns: LOCIZE_NS.COMMON })}
        </Button>
      </header>
      <div className="flex flex-col items-center px-12 pt-8">
        <img src={CircleCheck} alt="Circle Check" className="size-28 text-teal-60 mb-4" />
        <div className="flex flex-col items-center gap-2 justify-center mb-4">
          <h3 className="text-base font-medium text-denim-500 leading-5">
            {t('AppointmentsSuccess.title')}
          </h3>
          <p className="text-base text-gray-500 leading-5 text-center">{t(body)}</p>
        </div>
        {patientRecordCode ? (
          <div className="flex items-center gap-4 rounded-xl bg-mint-100 px-6 py-4 mb-4">
            <FolderOpenIcon className="size-10 shrink-0 text-teal-600" strokeWidth={1.2} />
            <div className="flex flex-col gap-1">
              <Text variant="text-sm-regular" className="text-gray-400">
                {t('AppointmentsSuccess.expedientNumber')}
              </Text>
              <Text variant="text-md-semibold" className="text-denim-800 min-h-5">
                {patientRecordCode}
              </Text>
            </div>
          </div>
        ) : (
          <RecordNumberSkeleton />
        )}
        <Button
          variant="ghost"
          className="w-fit text-sm hover:text-teal-500 hover:bg-transparent font-normal text-teal-600 flex items-center gap-1 leading-4"
          onClick={handleOnNavigate}
        >
          {t('AppointmentsSuccess.cta')}
          <ChevronRightIcon className="size-5" />
        </Button>
      </div>
    </>
  );
};
